import { all, select, takeEvery, fork, put, call } from 'redux-saga/effects'

declare global {
	interface Window {
		payVerse: any
	}
}

interface PayverseResultType {
	mallReserved: string
	orderId: string
	resultCode: string
	resultMessage: string
	resultStatus: string
	tid: string
}

interface KrwPaymentResultCallbackBodyType {
	mid: string
	clientKey: string
	reqDate: string
	sign: string
	orderId: string
	productName: string
	requestCurrency: string
	requestAmount: string
	returnUrl: string
	webhookUrl: string
}

// action
import { paymentActions, routeActions, shopActions } from '@redux/store/index'

// lib
import { Logger, ShippingBox, ShippingBoxSizeType, Fedex, Decimal } from '@utils'
import config from '@configs'
import { Validator, ValidatorResultType } from '@validators'
import { produce } from 'immer'
import { AsYouType, CountryCode } from 'libphonenumber-js'

// api
import { seasonProductApi, ResType, paymentApi, shippingApi, couponApi } from '@api'

// schemas
import {
	Coupon,
	SeasonProduct,
	SeasonProductType,
	ShippingCost,
	ShippingCostType,
	CustomerType,
} from '@schemas'

// type
import type {
	PaymentStateType,
	CartType,
	PaymentCustomerInfoType,
	PaymentDeliveryType,
	PaymentShippingType,
	PaymentCardType,
	PaymentCostType,
	MainMasterdataType,
} from '@redux/types'
import { CommitSharp } from '@mui/icons-material'

const apiGetSeasonProduct = async (params: {
	seasonId: string
	accessToken: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await seasonProductApi.getShopSeasonProduct({
		query: { seasonId: params.seasonId },
	})
	return res
}

const apiShippingCost = async (query: {
	weight: string
	width: string
	height: string
	depth: string
	countryIsoCode: string
	stateIsoCode: string
	city: string
	postalCode: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await shippingApi.getShippingCost({
		query: query,
	})
	return res
}

const apiGetCouponApply = async (query: { couponCode: string }) => {
	const res: ResType | undefined = await couponApi.getCouponApply({
		query: query,
	})
	return res
}

const apiCardCredit = async (body: {
	items: CartType[]
	customer: PaymentCustomerInfoType
	delivery: PaymentDeliveryType
	shipping: PaymentShippingType
}) => {
	const res: ResType | undefined = await paymentApi.cardCredit({
		body: body,
	})
	return res
}

const apiCardIntegrated = async (body: {
	items: CartType[]
	customer: PaymentCustomerInfoType
	delivery: PaymentDeliveryType
	shipping: PaymentShippingType
}) => {
	const res: ResType | undefined = await paymentApi.cardIntegrated({
		body: body,
	})
	return res
}

const apiCancelCardOrder = async (orderId: string) => {
	const res: ResType | undefined = await paymentApi.cancelCardOrder({
		body: {
			orderId: orderId,
		},
	})
	return res
}

function* triggerInitPaymentPageSaga() {
	try {
		yield put(paymentActions.PAYMENT_STATUS('loading'))

		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList

		if (cartList.length === 0) {
			yield put(paymentActions.PAYMENT_STATUS('init'))
			yield put(routeActions.NAVIGATE_PATH('/cart'))
		} else {
			const shopConfig = config.get('shop')
			const seasonId: string = shopConfig.seasonId
			const sessionAccessToken: string =
				sessionStorage.getItem('accessCustomerToken') ?? ''

			const apiRes: ResType = yield call(apiGetSeasonProduct, {
				seasonId: seasonId,
				accessToken: sessionAccessToken,
			})

			if (apiRes.status === 200) {
				const data = apiRes.data[0].productList as any[]
				const newSeaonsProductList: SeasonProductType[] = data.map(
					aData => new SeasonProduct(aData),
				)

				yield put(shopActions.PRODUCT_LIST(newSeaonsProductList))

				for (const anItem of cartList) {
					const idx = newSeaonsProductList.findIndex(
						aProduct => aProduct.id === anItem.id,
					)

					if (idx === -1) {
						yield put(
							paymentActions.MODAL_INFO({
								target: 'unexpectedError',
								title: 'Unexpected error',
							}),
						)
						yield put(paymentActions.SHOW_MODAL(true))
						yield put(
							paymentActions.TRY_TO_PAYMENT({
								cartList: [],
							}),
						)
						localStorage.removeItem('cart')
						window.dispatchEvent(new Event('storage'))
						break
					} else {
						const isCorrectSeasonId =
							newSeaonsProductList[idx].seasonId === anItem.seasonId
						const isCorrectProductId =
							newSeaonsProductList[idx].productId === anItem.productId
						const isCorrectSeasonUsd =
							newSeaonsProductList[idx].seasonUsd === anItem.seasonUsd
						const isCorrectQty =
							newSeaonsProductList[idx].seasonQuantity -
								newSeaonsProductList[idx].soldQuantity >=
							anItem.qty

						if (
							!isCorrectSeasonId ||
							!isCorrectProductId ||
							!isCorrectSeasonUsd ||
							!isCorrectQty
						) {
							yield put(
								paymentActions.MODAL_INFO({
									target: 'dataError',
									title: 'Data Error',
								}),
							)
							yield put(paymentActions.SHOW_MODAL(true))
							yield put(
								paymentActions.TRY_TO_PAYMENT({
									cartList: [],
								}),
							)

							localStorage.removeItem('cart')
							window.dispatchEvent(new Event('storage'))
						} else {
							yield put(paymentActions.PAYMENT_STATUS('ok'))
						}
					}
				}

				yield put(paymentActions.TRIGGER_GET_SHIPPING_COST(true))
			} else {
				yield put(
					paymentActions.MODAL_INFO({
						target: 'serverError',
						title: 'Server Error',
					}),
				)
				yield put(paymentActions.SHOW_MODAL(true))
				yield put(paymentActions.PAYMENT_STATUS('init'))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerUsdPayNowSaga() {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList
		const customerInfo: PaymentCustomerInfoType = yield select(
			state => state.payment.paymentCustomerInfo,
		)
		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		const shipping: PaymentShippingType = yield select(
			state => state.payment.paymentShipping,
		)
		const card: PaymentCardType = yield select(state => state.payment.paymentCard)
		const cost: PaymentCostType = yield select(state => state.payment.paymentCost)
		const errorMessages: PaymentStateType['paymentErrorMessages'] = yield select(
			state => state.payment.paymentErrorMessages,
		)

		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const customerId: string = customer.id

		const res: ValidatorResultType = Validator.payment({
			givenName: customerInfo.givenName,
			familyName: customerInfo.familyName,
			email: customerInfo.email,
			phoneCountryCode: customerInfo.phoneCountryCode,
			phoneNumber: customerInfo.phoneNumber,

			countryName: delivery.countryName,
			states: delivery.stateName,
			city: delivery.city,
			street1: delivery.street1,
			zip: delivery.zip,

			cardNumber: card.cardNumber,
			expiredMonth: card.expiredMonth,
			expiredYear: card.expiredYear,
			cvc: card.cvc,
			cardName: card.cardName,
		})

		const filterInvalidRes = Object.keys(res).filter(aKey => !res[aKey].isValid)
		const isSuccess = filterInvalidRes.length === 0

		const newErrorMessage = produce(errorMessages, draft => {
			draft.customer.givenName = res['givenName'].message
			draft.customer.familyName = res['familyName'].message
			draft.customer.email = res['email'].message
			draft.customer.phoneCountryCode = res['phoneCountryCode'].message
			draft.customer.phoneNumber = res['phoneNumber'].message

			draft.delivery.countryName = res['countryName'].message
			draft.delivery.states = res['states'].message
			draft.delivery.city = res['city'].message
			draft.delivery.street1 = res['street1'].message
			draft.delivery.zip = res['zip'].message

			draft.card.cardNumber = res['cardNumber'].message
			draft.card.expiredMonth = res['expiredMonth'].message
			draft.card.expiredYear = res['expiredYear'].message
			draft.card.cvc = res['cvc'].message
			draft.card.cardName = res['cardName'].message

			draft.isSuccess = isSuccess
		})

		yield put(paymentActions.PAYMENT_ERROR_MESSAGES({ ...newErrorMessage }))

		if (isSuccess) {
			const asYouType = new AsYouType()
			const newPhoneNumber = asYouType.input(
				`+${customerInfo.phoneCountryCode}${customerInfo.phoneNumber}`,
			)
			const phoneNumber = asYouType.getNationalNumber()

			const body = {
				items: cartList,
				customer: {
					...customerInfo,
					phoneNumber: phoneNumber,
				},
				delivery: delivery,
				shipping: shipping,
				card: card,
				cost: cost,
				customerId: customerId,
			}

			const apiRes: ResType = yield call(apiCardCredit, body)

			if (apiRes.status === 201) {
				const data = apiRes.data
				const orderId: string = data[0].orderId
				const orderDetail = cartList
					.map((anItem: CartType) => `${anItem.productName} x ${anItem.qty}`)
					.join(',\n')
				const orderAddress =
					delivery.countryIsoCode === 'US'
						? [
								delivery.street2,
								delivery.street1,
								delivery.city,
								delivery.stateIsoCode,
								delivery.countryName,
						  ].join(' ')
						: [
								delivery.street2,
								delivery.street1,
								delivery.city,
								delivery.stateName,
								delivery.countryName,
						  ].join(' ')

				yield put(
					paymentActions.PAYMENT_SUCCESS({
						orderId: orderId,
						orderDetail: orderDetail,
						address: orderAddress,
						email: customerInfo.email,
						phoneNumber: newPhoneNumber,
					}),
				)

				localStorage.setItem('cart', '')
				window.dispatchEvent(new Event('storage'))

				yield put(paymentActions.TRIGGER_RESET_PAYMENT_DATA(true))
				yield put(routeActions.NAVIGATE_PATH('/payment-success'))
			} else {
				if (
					apiRes.exception.message.meta.title ===
					'[CARD-CREDIT-CONTROLLER]:PAYVERSE-FAILED'
				) {
					const errorMessage = apiRes.exception.message.meta.detail.resultMessage

					yield put(
						paymentActions.MODAL_INFO({
							target: 'paymentError',
							title: 'Payment Error',
						}),
					)
					yield put(paymentActions.SHOW_MODAL(true))
				}
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

const krwPaymentResultCallback = (body: KrwPaymentResultCallbackBodyType) => {
	if (!window.payVerse) {
		alert('Card payment error occurred.')
		return
	}

	return new Promise((resolve, reject) => {
		window.payVerse.requestUI(
			{
				...body,
				returnUrl: 'https://judahs.io/payment',
				webhookUrl: 'https://api.judahs.io/latest/payment/card-integrated-webhook',
			},
			(payverseResult: PayverseResultType) => {
				resolve(payverseResult)
			},
		)
	})
}

function* triggerKrwPayNowSaga() {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList
		const customerInfo: PaymentCustomerInfoType = yield select(
			state => state.payment.paymentCustomerInfo,
		)
		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		const shipping: PaymentShippingType = yield select(
			state => state.payment.paymentShipping,
		)
		const cost: PaymentCostType = yield select(state => state.payment.paymentCost)
		const errorMessages: PaymentStateType['paymentErrorMessages'] = yield select(
			state => state.payment.paymentErrorMessages,
		)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const customerId: string = customer.id

		paymentActions.PAYMENT_SUCCESS({
			orderId: '',
			orderDetail: '',
			address: '',
			email: '',
			phoneNumber: '',
		})

		const res: ValidatorResultType = Validator.payment({
			givenName: customerInfo.givenName,
			familyName: customerInfo.familyName,
			email: customerInfo.email,
			phoneCountryCode: customerInfo.phoneCountryCode,
			phoneNumber: customerInfo.phoneNumber,

			countryName: delivery.countryName,
			states: delivery.stateName,
			city: delivery.city,
			street1: delivery.street1,
			zip: delivery.zip,
		})

		const filterInvalidRes = Object.keys(res).filter(aKey => !res[aKey].isValid)
		const isSuccess = filterInvalidRes.length === 0

		const newErrorMessage = produce(errorMessages, draft => {
			draft.customer.givenName = res['givenName'].message
			draft.customer.familyName = res['familyName'].message
			draft.customer.email = res['email'].message
			draft.customer.phoneCountryCode = res['phoneCountryCode'].message
			draft.customer.phoneNumber = res['phoneNumber'].message

			draft.delivery.countryName = res['countryName'].message
			draft.delivery.states = res['states'].message
			draft.delivery.city = res['city'].message
			draft.delivery.street1 = res['street1'].message
			draft.delivery.zip = res['zip'].message

			draft.isSuccess = isSuccess
		})

		yield put(paymentActions.PAYMENT_ERROR_MESSAGES({ ...newErrorMessage }))

		if (isSuccess) {
			const asYouType = new AsYouType(
				delivery.countryIsoCode.toUpperCase() as CountryCode,
			)
			const newPhoneNumber = asYouType.input(customerInfo.phoneNumber)
			const phoneNumber = asYouType.getNationalNumber()

			const body = {
				items: cartList,
				customer: {
					...customerInfo,
					phoneNumber: phoneNumber,
				},
				delivery: delivery,
				shipping: shipping,
				cost: cost,
				customerId: customerId,
			}

			const apiRes: ResType = yield call(apiCardIntegrated, body)

			if (apiRes.status === 201) {
				const data = apiRes.data
				const resBody: KrwPaymentResultCallbackBodyType = {
					mid: data[0].mid,
					clientKey: data[0].clientKey,
					reqDate: data[0].reqDate,
					sign: data[0].sign,
					orderId: data[0].orderId,
					productName: data[0].productName,
					requestCurrency: data[0].requestCurrency,
					requestAmount: data[0].requestAmount,
					returnUrl: data[0].returnUrl,
					webhookUrl: data[0].webhookUrl,
				}

				const orderId: string = data[0].orderId

				// initData에는 { mid, clientKey, reqDate, sign, orderId, productName, requestCurrency, requestAmount } 등이 들어있음
				const payverseResult: PayverseResultType = yield call(
					krwPaymentResultCallback,
					resBody,
				)

				if (payverseResult.resultStatus === 'SUCCESS') {
					const orderDetail = cartList
						.map((anItem: CartType) => `${anItem.productName} x ${anItem.qty}`)
						.join(',\n')
					const orderAddress =
						delivery.countryIsoCode === 'KR'
							? `대한민국 ${delivery.stateName} ${delivery.city} \n${delivery.street1} \n${delivery.street2}`
							: [
									delivery.street2,
									delivery.street1,
									delivery.city,
									delivery.stateName,
									delivery.countryName,
							  ].join(' ')

					yield put(
						paymentActions.PAYMENT_SUCCESS({
							orderId: orderId,
							orderDetail: orderDetail,
							address: orderAddress,
							email: customerInfo.email,
							phoneNumber: newPhoneNumber,
						}),
					)

					localStorage.setItem('cart', '')
					window.dispatchEvent(new Event('storage'))

					yield put(paymentActions.TRIGGER_RESET_PAYMENT_DATA(true))
					yield put(routeActions.NAVIGATE_PATH('/payment-success'))
				} else if (payverseResult.resultStatus !== 'SUCCESS') {
					yield call(apiCancelCardOrder, orderId)
				}
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerCouponApplySaga() {
	try {
		const cost: PaymentCostType = yield select(state => state.payment.paymentCost)

		const query = {
			couponCode: cost.couponCode,
		}

		const apiRes: ResType = yield call(apiGetCouponApply, query)

		if (apiRes.status === 200) {
			const data = apiRes.data[0] as any
			const coupon = new Coupon({ ...data })

			if (coupon.status === 'active') {
				const newCost: PaymentCostType = {
					...cost,
					discountedUsd: coupon.discountUsd,
					discountedKrw: coupon.discountKrw,
				}
				yield put(paymentActions.PAYMENT_COST(newCost))
				yield put(paymentActions.PAYMENT_COUPON_ERROR_MESSAGE(''))
			} else {
				const newCost: PaymentCostType = { ...cost, discountedUsd: 0 }
				yield put(paymentActions.PAYMENT_COST(newCost))

				const errorMessage =
					'This coupon is no longer available for use. We appreciate your understanding!'
				yield put(paymentActions.PAYMENT_COUPON_ERROR_MESSAGE(errorMessage))
			}
		} else {
			const newCost: PaymentCostType = { ...cost, discountedUsd: 0, discountedKrw: 0 }
			yield put(paymentActions.PAYMENT_COST(newCost))

			const errorMessage =
				apiRes.exception.message.meta.title === '[COUPON-SERVICE]: No Coupon'
					? 'This coupon does not exist.'
					: apiRes.exception.message.meta.title

			yield put(paymentActions.PAYMENT_COUPON_ERROR_MESSAGE(errorMessage))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerGetShippingCostSaga() {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		// const master: MainMasterdataType = yield select(
		// 	state => state.masterdata.mainMasterdata,
		// )

		// const exchangeRate: number = master.exchangeRate < 500 ? 1470 : master.exchangeRate
		// const fedexSurchargeRate: number = master.fedexFuelSurchargeRate
		const cartList: CartType[] = tryToPayment.cartList

		const filterKdm6 = cartList.filter(aCart => aCart.productCode === 'KDM6')
		const filterKdm4 = cartList.filter(aCart => aCart.productCode === 'KDM4')
		const kdm6Qty = filterKdm6.length === 0 ? 0 : filterKdm6[0].qty
		const kdm4Qty = filterKdm4.length === 0 ? 0 : filterKdm4[0].qty
		const size: ShippingBoxSizeType = ShippingBox.getSize({
			kdm6Qty: kdm6Qty,
			kdm4Qty: kdm4Qty,
		})

		if (delivery.countryIsoCode === 'KR') {
			const newShipping = {
				companyName: 'local',
				deliveryOption: '',
				usd: 0,
				deliveryDays: 0,
				boxType: size.boxType,
				rateType: '',
			}

			yield put(paymentActions.PAYMENT_SHIPPING(newShipping))
			yield put(paymentActions.SHIPPING_COST_LIST([]))
		} else if (
			cartList.length > 0 &&
			delivery.countryIsoCode !== '' &&
			delivery.zip !== '' &&
			delivery.stateIsoCode !== '' &&
			delivery.city !== ''
		) {
			yield put(paymentActions.SHIPPING_COST_STATUS('loading'))

			const { zone, rateKrw } = Fedex.getShippingCost({
				countryCode: delivery.countryIsoCode,
				stateCode: delivery.stateIsoCode,
				zip: delivery.zip,
				width_cm: size.width_cm,
				height_cm: size.height_cm,
				depth_cm: size.depth_cm,
			})

			// const tempRate = Decimal(rateKrw / exchangeRate) * (1 + fedexSurchargeRate / 100)

			const isFicp = zone.isAvailable ? 1 : 0

			const subtotal: {
				sumUsd: number
				vatUsd: number
				qty: number
			} = cartList.reduce(
				(sum, { sumUsd, vatUsd, qty }) => {
					return {
						sumUsd: Decimal(sum.sumUsd + sumUsd),
						vatUsd: Decimal(sum.vatUsd + vatUsd),
						qty: sum.qty + qty,
					}
				},
				{
					sumUsd: 0,
					vatUsd: 0,
					qty: 0,
				},
			)

			const newShippingCost = subtotal.sumUsd < 100 ? 20 : 0

			const newShipping = {
				companyName: 'fedex',
				deliveryOption: '',
				usd: newShippingCost,
				deliveryDays: 0,
				boxType: size.boxType,
				rateType:
					isFicp === 1 ? 'FEDEX_INTERNATIONAL_CONNECT_PLUS' : 'INTERNATIONAL_ECONOMY',
			}

			yield put(paymentActions.PAYMENT_SHIPPING(newShipping))

			yield put(paymentActions.SHIPPING_COST_STATUS('ok'))

			// const query = {
			// 	weight: size.weight_kg.toString(),
			// 	width: size.width_cm.toString(),
			// 	height: size.height_cm.toString(),
			// 	depth: size.depth_cm.toString(),
			// 	countryIsoCode: delivery.countryIsoCode,
			// 	stateIsoCode: delivery.stateIsoCode,
			// 	city: delivery.city,
			// 	postalCode: delivery.zip,
			// 	isFicp: isFicp.toString(),
			// }

			// const apiRes: ResType = yield call(apiShippingCost, query)

			// if (apiRes.status === 200) {
			// 	const data: ShippingCostType[] = (apiRes.data as any[]).map(
			// 		aData => new ShippingCost(aData),
			// 	)

			// 	const newShipping = {
			// 		companyName: 'fedex',
			// 		deliveryOption: '',
			// 		usd: data[0].rate,
			// 		deliveryDays: 0,
			// 		boxType: size.boxType,
			// 		rateType:
			// 			isFicp === 1 ? 'FEDEX_INTERNATIONAL_CONNECT_PLUS' : 'INTERNATIONAL_ECONOMY',
			// 	}

			// 	yield put(paymentActions.PAYMENT_SHIPPING(newShipping))

			// 	// yield put(paymentActions.SHIPPING_COST_LIST(data))
			// 	// yield put(paymentActions.SHIPPING_COST_STATUS('ok'))
			// } else {
			// 	// yield put(paymentActions.SHIPPING_COST_LIST([]))
			// 	// yield put(paymentActions.SHIPPING_COST_STATUS('empty'))
			// }
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerResetPaymentDataSaga() {
	try {
		yield put(
			paymentActions.TRY_TO_PAYMENT({
				cartList: [],
			}),
		)

		yield put(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				givenName: '',
				familyName: '',
				email: '',
				phoneCountryCode: '',
				phoneNumber: '',
			}),
		)

		yield put(
			paymentActions.PAYMENT_DELIVERY({
				countryName: '',
				countryIsoCode: '',
				stateName: '',
				stateIsoCode: '',
				city: '',
				street1: '',
				street2: '',
				zip: '',
			}),
		)

		yield put(
			paymentActions.PAYMENT_SHIPPING({
				companyName: '',
				deliveryOption: '',
				usd: 0,
				deliveryDays: 0,
				boxType: 'A',
				rateType: '',
			}),
		)

		yield put(
			paymentActions.PAYMENT_COST({
				totalUsd: 0,
				vatUsd: 0,
				shippingUsd: 0,
				discountedUsd: 0,
				discountedKrw: 0,
				paidUsd: 0,
				couponCode: '',
			}),
		)
	} catch (error) {
		Logger.warn(error)
	}
}

function* tryToPaymentSaga() {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList

		if (cartList.length > 0) {
			yield put(routeActions.NAVIGATE_PATH('/payment'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* paymentDeliverySaga() {
	try {
		yield put(paymentActions.TRIGGER_GET_SHIPPING_COST(true))
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(paymentActions.TRIGGER_INIT_PAYMENT_PAGE, triggerInitPaymentPageSaga)
	yield takeEvery(paymentActions.TRIGGER_USD_PAY_NOW, triggerUsdPayNowSaga)
	yield takeEvery(paymentActions.TRIGGER_KRW_PAY_NOW, triggerKrwPayNowSaga)
	yield takeEvery(paymentActions.TRIGGER_COUPON_APPLY, triggerCouponApplySaga)
	yield takeEvery(paymentActions.TRIGGER_GET_SHIPPING_COST, triggerGetShippingCostSaga)
	yield takeEvery(paymentActions.TRIGGER_RESET_PAYMENT_DATA, triggerResetPaymentDataSaga)

	yield takeEvery(paymentActions.TRY_TO_PAYMENT, tryToPaymentSaga)

	yield takeEvery(paymentActions.PAYMENT_DELIVERY, paymentDeliverySaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
